'use client'

import * as React from 'react'

// "In this article" inline table of contents — cobalt blog system.
// A collapsible <details> card (mockup: white rounded card, uppercase
// label, cobalt-on-hover links). Used for the focus layout and as the
// tablet/mobile fallback when a sticky rail is present (the page passes
// `lg:hidden` so the rail owns wide screens).
//
// A scrollspy still tracks the active section so the link the reader is
// inside reads in cobalt. Renders nothing below 3 items, matching the
// auto-inject rule in article-blocks-renderer.

export function ArticleTOC({
  items,
  className,
}: {
  items: Array<{ id: string; text: string }>
  className?: string
}) {
  const [active, setActive] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (items.length < 3) return
    const headings = items
      .map((it) => document.getElementById(it.id))
      .filter((el): el is HTMLElement => el !== null)
    if (headings.length === 0) return

    const update = () => {
      const line = window.innerHeight * 0.35
      let current: string | null = null
      for (const h of headings) {
        if (h.getBoundingClientRect().top <= line) current = h.id
      }
      setActive(current)
    }
    const observer = new IntersectionObserver(update, {
      rootMargin: '0px 0px -65% 0px',
      threshold: [0, 1],
    })
    for (const h of headings) observer.observe(h)
    update()
    return () => observer.disconnect()
  }, [items])

  if (items.length < 3) return null
  return (
    <details className={`not-prose group my-8 rounded-2xl border border-border bg-card px-[18px] py-1.5 ${className || ''}`}>
      <summary className="cursor-pointer list-none py-3 text-xs font-bold uppercase tracking-[0.1em] text-foreground [&::-webkit-details-marker]:hidden">
        In this article
      </summary>
      <nav className="flex flex-col gap-2.5 pb-3.5">
        {items.map((it) => (
          <a
            key={it.id}
            href={`#${it.id}`}
            aria-current={active === it.id ? 'true' : undefined}
            className={`text-[15px] transition-colors duration-[var(--dur-fast)] ${
              active === it.id
                ? 'font-medium text-primary'
                : 'text-muted-foreground hover:text-primary'
            }`}
          >
            {it.text}
          </a>
        ))}
      </nav>
    </details>
  )
}
